/**
 * TestCaseQualityCard - 테스트케이스 품질 분석 카드
 * 
 * "좋은 테스트케이스인가?"를 평가하는 컴포넌트
 * - 기획 의도 커버리지
 * - 리스크 대응 커버리지
 * - 엣지케이스 충분성
 * 
 * MAX 플랜 전용 AI 리포트에 사용
 */

import { useLanguage } from '../i18n';
import { stripBullet } from '../utils';

interface CoverageItem {
  intent: string;       // 기획 의도
  covered: boolean;     // 테스트케이스에서 커버되는지
  testCases?: string[]; // 관련 테스트케이스들
}

interface TestCaseQualityProps {
  // 기획 의도 커버리지
  planningCoverage: {
    items: CoverageItem[];
    score: number | null;  // 0-100, 데이터 없으면 null
  };
  // 리스크 커버리지
  riskCoverage: {
    items: CoverageItem[];
    score: number | null;
  };
  // 테스트케이스 품질 점수
  qualityScore: number | null;  // 0-100, 데이터 없으면 null
  // 개선 제안
  suggestions: string[];
}

export default function TestCaseQualityCard({
  planningCoverage,
  riskCoverage,
  qualityScore,
  suggestions,
}: TestCaseQualityProps) {
  const { lang } = useLanguage();
  const hasData =
    (planningCoverage.items?.length ?? 0) > 0 ||
    (riskCoverage.items?.length ?? 0) > 0 ||
    qualityScore !== null;
  
  // 점수 색상
  const getScoreColor = (score: number | null) => {
    if (score === null) return 'text-terminal-muted';
    if (score >= 80) return 'text-status-go';
    if (score >= 60) return 'text-status-warning';
    return 'text-status-danger';
  };
  
  const getScoreBg = (score: number | null) => {
    if (score === null) return 'bg-terminal-dim/10 border-terminal-border';
    if (score >= 80) return 'bg-status-go/10 border-status-go/30';
    if (score >= 60) return 'bg-status-warning/10 border-status-warning/30';
    return 'bg-status-danger/10 border-status-danger/30';
  };
  
  // 품질 등급
  const getQualityGrade = (score: number | null) => {
    if (score === null) return { grade: '—', label: lang === 'ko' ? '데이터 없음' : 'No data' };
    if (score >= 90) return { grade: 'A+', label: lang === 'ko' ? '매우 우수' : 'Excellent' };
    if (score >= 80) return { grade: 'A', label: lang === 'ko' ? '우수' : 'Good' };
    if (score >= 70) return { grade: 'B', label: lang === 'ko' ? '양호' : 'Fair' };
    if (score >= 60) return { grade: 'C', label: lang === 'ko' ? '보통' : 'Average' };
    return { grade: 'D', label: lang === 'ko' ? '개선 필요' : 'Needs Work' };
  };
  
  const qualityGrade = getQualityGrade(qualityScore);
  
  return (
    <div className="space-y-4">
      {/* 헤더 - 품질 점수 */}
      <div className={`p-4 rounded-lg border ${getScoreBg(qualityScore)}`}>
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-terminal-text mb-1">
              {lang === 'ko' ? '테스트케이스 품질 평가' : 'Test Case Quality'}
            </h4>
            <p className="text-xs text-terminal-muted">
              {lang === 'ko' 
                ? '"좋은 테스트케이스인가?"에 대한 AI 분석'
                : 'AI analysis: "Are these good test cases?"'}
            </p>
            {!hasData && (
              <p className="text-xs text-terminal-muted mt-2">
                {lang === 'ko'
                  ? '테스트케이스(임포트/AI 제안/직접 입력)가 없어 커버리지 분석을 표시할 수 없습니다.'
                  : 'No test cases found, so coverage analysis is unavailable.'}
              </p>
            )}
          </div>
          <div className="text-center">
            <div className={`text-3xl font-bold ${getScoreColor(qualityScore)}`}>
              {qualityGrade.grade}
            </div>
            <div className="text-xs text-terminal-muted">
              {qualityScore === null ? '—/100' : `${qualityScore}/100`}
            </div>
          </div>
        </div>
        
        {/* 프로그레스 바 */}
        <div className="mt-3">
          <div className="h-2 bg-terminal-dim/30 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-1000 ease-out ${
                qualityScore === null ? 'bg-terminal-dim/40' :
                qualityScore >= 80 ? 'bg-status-go' :
                qualityScore >= 60 ? 'bg-status-warning' : 'bg-status-danger'
              }`}
              style={{ width: `${qualityScore ?? 0}%` }}
            />
          </div>
        </div>
      </div>
      
      {/* 기획 의도 커버리지 */}
      <div className="p-4 bg-terminal-dim/10 rounded-lg border border-terminal-border">
        <div className="flex items-center justify-between mb-3">
          <h5 className="text-sm font-medium text-terminal-text flex items-center gap-2">
            <span>📋</span>
            {lang === 'ko' ? '기획 의도 커버리지' : 'Planning Intent Coverage'}
          </h5>
          <span className={`text-sm font-bold ${getScoreColor(planningCoverage.score)}`}>
            {planningCoverage.score === null ? '—' : `${planningCoverage.score}%`}
          </span>
        </div>
        
        <div className="space-y-2">
          {planningCoverage.items.length === 0 ? (
            <div className="text-sm text-terminal-muted">
              {lang === 'ko' ? '표시할 항목이 없습니다.' : 'No items to display.'}
            </div>
          ) : (
          planningCoverage.items.map((item, i) => (
            <div 
              key={i} 
              className={`flex items-start gap-2 p-2 rounded ${
                item.covered ? 'bg-status-go/5' : 'bg-status-danger/5'
              }`}
            >
              <span className={`flex-shrink-0 ${item.covered ? 'text-status-go' : 'text-status-danger'}`}>
                {item.covered ? '✓' : '✗'}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-terminal-text">{stripBullet(item.intent)}</p>
                {item.testCases && item.testCases.length > 0 && (
                  <p className="text-xs text-terminal-muted mt-0.5 truncate">
                    → {item.testCases.join(', ')}
                  </p>
                )}
              </div>
            </div>
          ))
          )}
        </div>
      </div>
      
      {/* 리스크 커버리지 */}
      <div className="p-4 bg-terminal-dim/10 rounded-lg border border-terminal-border">
        <div className="flex items-center justify-between mb-3">
          <h5 className="text-sm font-medium text-terminal-text flex items-center gap-2">
            <span>⚠️</span>
            {lang === 'ko' ? '리스크 대응 커버리지' : 'Risk Coverage'}
          </h5>
          <span className={`text-sm font-bold ${getScoreColor(riskCoverage.score)}`}>
            {riskCoverage.score === null ? '—' : `${riskCoverage.score}%`}
          </span>
        </div>
        
        <div className="space-y-2">
          {riskCoverage.items.length === 0 ? (
            <div className="text-sm text-terminal-muted">
              {lang === 'ko' ? '표시할 항목이 없습니다.' : 'No items to display.'}
            </div>
          ) : (
          riskCoverage.items.map((item, i) => (
            <div 
              key={i} 
              className={`flex items-start gap-2 p-2 rounded ${
                item.covered ? 'bg-status-go/5' : 'bg-status-danger/5'
              }`}
            >
              <span className={`flex-shrink-0 ${item.covered ? 'text-status-go' : 'text-status-danger'}`}>
                {item.covered ? '✓' : '✗'}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-terminal-text">{stripBullet(item.intent)}</p>
                {item.testCases && item.testCases.length > 0 && (
                  <p className="text-xs text-terminal-muted mt-0.5 truncate">
                    → {item.testCases.join(', ')}
                  </p>
                )}
              </div>
            </div>
          ))
          )}
        </div>
      </div>
      
      {/* 개선 제안 */}
      {suggestions.length > 0 && (
        <div className="p-4 bg-status-warning/5 rounded-lg border border-status-warning/30">
          <h5 className="text-sm font-medium text-status-warning mb-3 flex items-center gap-2">
            <span>💡</span>
            {lang === 'ko' ? '테스트케이스 개선 제안' : 'Improvement Suggestions'}
          </h5>
          <ul className="space-y-2">
            {suggestions.map((suggestion, i) => (
              <li key={i} className="text-sm text-terminal-muted flex items-start gap-2">
                <span className="text-status-warning flex-shrink-0">→</span>
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

